import { logical } from "../../ui/kit/metrics.ts";
import { ABILITY_SLOT, BAR_SLOT_COUNT } from "./layout.ts";
import { STATUS_MAX, EXPIRING_MS, type BarSource, type RowTimes } from "./store.ts";
import { COOLDOWN_TEXTS, MAX_SECONDS, TIME_LEFT_TEXTS, TIP_TIME_TEXTS } from "./words.ts";

/**
 * The bar's per-frame writer (P2.2-T4): once an animation frame, it reads the store's times (`BarTimes`) and writes the
 * few DOM values that change by the frame, the cooldown wipe's height in whole logical pixels, the seconds on it and
 * the rows' times left, each only when what it shows changes, and only strings built at load (`words.ts`, `WIPE_HEIGHTS`),
 * so a frame allocates nothing. Every time is a whole number of ms from the store's origin and every step of the sums
 * is a small integer: the wipe is found by comparing products, and a second shown steps down from the one before, so
 * no frame makes a fractional number V8 would box. React draws everything else, and only when the store changes. It
 * also ends the cast bar's reason when its time is up. On the game page the game's step drives it with the frame's
 * time, so nothing of its own is asked of the browser a frame; the gallery's runs on an animation frame of its own,
 * which starts and stops with the bar.
 */

/**
 * The wipe's height as a CSS length, for every whole logical pixel of the taller well, a slot's inside its 1 pixel edge
 * when it holds colour art.
 */
const WIPE_HEIGHTS: readonly string[] = Array.from({ length: ABILITY_SLOT.artWell + 1 }, (_, px) => logical(px));

/**
 * A second in ms.
 */
const SECOND_MS = 1000;

/**
 * The elements of one slot the ticker writes: the slot (its `data-cooling` attribute, which React never sets, so a
 * render never clears it), the wipe and the seconds on it; and the height in logical pixels the wipe covers when full,
 * the slot's well (`ABILITY_SLOT.well`), or its inside its 1 pixel edge while it holds colour art (`artWell`, ADR-111
 * decision 19).
 */
export interface SlotElements {
  readonly slot: HTMLElement;
  readonly wipe: HTMLElement;
  readonly seconds: HTMLElement;
  readonly well: number;
}

/**
 * The elements of one status icon the ticker writes: the icon (its `data-expiring` attribute) and its time.
 */
export interface StatusElements {
  readonly icon: HTMLElement;
  readonly time: HTMLElement;
}

/**
 * The height of a cooldown's wipe in whole logical pixels: the well's share of the time left, rounded up, so the wipe
 * holds at least a pixel until the end the server's tick sets and never empties early. It is the least height whose
 * share of the length reaches the time left, found by comparing whole products.
 *
 * @param leftMs The time left in whole ms.
 * @param lengthMs The cooldown's length in whole ms.
 * @param well The height the wipe covers when full, the slot's well unless given.
 * @returns 0 to the well's height.
 */
export function wipeHeight(leftMs: number, lengthMs: number, well: number = ABILITY_SLOT.well): number {
  if (leftMs <= 0 || lengthMs <= 0) {
    return 0;
  }
  const scaled = well * leftMs;
  let height = well;
  while (height > 1 && (height - 1) * lengthMs >= scaled) {
    height -= 1;
  }
  return height;
}

/**
 * The whole seconds a time left shows, rounded up and held to the tables: stepped down from the seconds shown before
 * while the time has passed below them, so a frame only compares products; worked out afresh only when nothing was
 * shown or the time rose past it (a new cooldown, a refresh), which an event causes, not a frame.
 *
 * @param leftMs The time left in whole ms.
 * @param shown The seconds shown before, 0 or less for none.
 * @returns The seconds, 0 for none left.
 */
export function stepSeconds(leftMs: number, shown: number): number {
  if (leftMs <= 0) {
    return 0;
  }
  if (shown <= 0 || leftMs > shown * SECOND_MS) {
    return Math.min(MAX_SECONDS, Math.ceil(leftMs / SECOND_MS));
  }
  let seconds = shown;
  while (seconds > 1 && leftMs <= (seconds - 1) * SECOND_MS) {
    seconds -= 1;
  }
  return seconds;
}

/**
 * The ticker.
 */
export class BarTicker {
  private readonly slots: (SlotElements | null)[] = new Array<SlotElements | null>(BAR_SLOT_COUNT).fill(null);
  private readonly wipeStyles: (CSSStyleDeclaration | null)[] = new Array<CSSStyleDeclaration | null>(BAR_SLOT_COUNT).fill(null);
  private readonly wipes = new Int32Array(BAR_SLOT_COUNT).fill(-1);
  private readonly seconds = new Int32Array(BAR_SLOT_COUNT).fill(-1);
  private readonly secondsLeft = new Int32Array(BAR_SLOT_COUNT);
  private readonly own: (StatusElements | null)[] = new Array<StatusElements | null>(STATUS_MAX).fill(null);
  private readonly target: (StatusElements | null)[] = new Array<StatusElements | null>(STATUS_MAX).fill(null);
  private readonly ownShown = new Int32Array(STATUS_MAX).fill(-1);
  private readonly targetShown = new Int32Array(STATUS_MAX).fill(-1);
  private tipRow: "own" | "target" | null = null;
  private tipIndex = -1;
  private tipTime: HTMLElement | null = null;
  private tipShown = -1;
  private slotTipIndex = -1;
  private slotTipRow: HTMLElement | null = null;
  private slotTipTime: HTMLElement | null = null;
  private slotTipShown = -1;
  private frame = 0;

  /**
   * @param source The store whose times it reads.
   * @param clock A clock to read in place of the animation frame's time, which the gallery holds still; null on the
   *   page.
   * @param driven Whether something else calls `write` each frame (the game's step on the page), so `start` asks for
   *   no animation frame of its own.
   */
  public constructor(
    private readonly source: BarSource,
    private readonly clock: (() => number) | null = null,
    private readonly driven = false,
  ) {}

  /**
   * Binds or unbinds a slot's elements, as React mounts and unmounts them; a bound slot is written on the next frame.
   * The wipe's style declaration is held from here, so a frame that writes the wipe never asks the element for it and
   * the browser never makes its wrapper again after a collection.
   *
   * @param index The slot.
   * @param elements Its elements, or null.
   */
  public bindSlot(index: number, elements: SlotElements | null): void {
    this.slots[index] = elements;
    this.wipeStyles[index] = elements === null ? null : elements.wipe.style;
    this.wipes[index] = -1;
    this.seconds[index] = -1;
    this.secondsLeft[index] = 0;
  }

  /**
   * Binds or unbinds a status icon's elements, as React mounts and unmounts them.
   *
   * @param row The player's row or its target's.
   * @param index The icon's place.
   * @param elements Its elements, or null.
   */
  public bindStatus(row: "own" | "target", index: number, elements: StatusElements | null): void {
    (row === "own" ? this.own : this.target)[index] = elements;
    (row === "own" ? this.ownShown : this.targetShown)[index] = -1;
  }

  /**
   * Binds or unbinds the open tooltip's time left (P2.2-T12): the element is written with the seconds its icon shows (`TIP_TIME_TEXTS`),
   * whenever they change, so an open tooltip counts down with its icon and no frame allocates.
   *
   * @param row The icon's row, or null to unbind.
   * @param index The icon's place.
   * @param time The tooltip's time element, or null to unbind.
   */
  public bindTip(row: "own" | "target" | null, index: number, time: HTMLElement | null): void {
    this.tipRow = time === null ? null : row;
    this.tipIndex = index;
    this.tipTime = time;
    this.tipShown = -1;
  }

  /**
   * Binds or unbinds the open item card's "Ready in" row (P2.8-T55): while its slot cools the row is marked
   * `data-cooling` and its time is written with the whole seconds the slot counts (`TIP_TIME_TEXTS`), whenever they
   * change; at 0 the mark is taken off, so the row is not drawn while the item is ready, and no frame allocates.
   * @param index The slot, or -1 to unbind.
   * @param row The row, or null to unbind.
   * @param time The row's time element, or null to unbind.
   */
  public bindSlotTip(index: number, row: HTMLElement | null, time: HTMLElement | null): void {
    const bound = row !== null && time !== null;
    this.slotTipIndex = bound ? index : -1;
    this.slotTipRow = bound ? row : null;
    this.slotTipTime = bound ? time : null;
    this.slotTipShown = -1;
  }

  /**
   * Starts writing on every animation frame, where the page has them, unless something else drives it.
   */
  public start(): void {
    if (!this.driven && this.frame === 0 && typeof requestAnimationFrame === "function") {
      this.frame = requestAnimationFrame(this.step);
    }
  }

  /**
   * Stops writing.
   */
  public stop(): void {
    if (this.frame !== 0) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  /**
   * Writes what changed since the last frame: each slot's wipe and seconds, and each icon's time and expiring state. A
   * frame's time may come before the receipt that started a time
   * (the frame began before the message was read), so a time left is held to what it was at its receipt. The cast bar's
   * reason is ended by the store's timer, never here.
   *
   * @param nowMs The page clock.
   */
  public write(nowMs: number): void {
    const times = this.source.times;
    const now = Math.floor(nowMs) - times.origin;
    for (let index = 0; index < BAR_SLOT_COUNT; index += 1) {
      const elements = this.slots[index];
      if (elements === null || elements === undefined) {
        continue;
      }
      const length = times.cooldownLength[index] as number;
      const left = Math.min(length, (times.cooldownEnd[index] as number) - now);
      const height = wipeHeight(left, length, elements.well);
      const before = this.wipes[index] as number;
      if (height !== before) {
        if (height === 0 || before <= 0) {
          elements.slot.toggleAttribute("data-cooling", height > 0);
        }
        (this.wipeStyles[index] as CSSStyleDeclaration).height = WIPE_HEIGHTS[height] as string;
        this.wipes[index] = height;
      }
      const seconds = stepSeconds(left, this.secondsLeft[index] as number);
      this.secondsLeft[index] = seconds;
      if (index === this.slotTipIndex && seconds !== this.slotTipShown) {
        this.writeSlotTip(seconds);
      }
      const shown = height >= ABILITY_SLOT.secondsFrom ? seconds : 0;
      if (shown !== this.seconds[index]) {
        elements.seconds.textContent = shown === 0 ? "" : (COOLDOWN_TEXTS[shown] as string);
        this.seconds[index] = shown;
      }
    }
    this.writeRow(times.own, this.own, this.ownShown, times.frozen, now, this.tipRow === "own" ? this.tipIndex : -1);
    this.writeRow(times.target, this.target, this.targetShown, false, now, this.tipRow === "target" ? this.tipIndex : -1);
  }

  /**
   * Writes the open item card's time left and marks its row cooling, or takes the mark off at 0.
   * @param seconds The whole seconds left, 0 for none.
   */
  private writeSlotTip(seconds: number): void {
    const row = this.slotTipRow as HTMLElement;
    if (seconds === 0 || this.slotTipShown <= 0) {
      row.toggleAttribute("data-cooling", seconds > 0);
    }
    if (seconds > 0) {
      (this.slotTipTime as HTMLElement).textContent = TIP_TIME_TEXTS[seconds] as string;
    }
    this.slotTipShown = seconds;
  }

  /**
   * Writes a row's times: whole seconds left, at least 1 while the effect is listed (the server ends it, not the page),
   * into each icon and the open tooltip's time,
   * and the expiring state in an effect's last `EXPIRING_MS`, never while its bearer is dead. A shown value packs the
   * seconds and the expiring bit, so each is written only when one changes.
   *
   * @param times The row's times.
   * @param elements The row's bound icons.
   * @param shown What each icon shows now.
   * @param frozen Whether the bearer is dead.
   * @param now The ms since the origin.
   * @param tip The place of the icon whose tooltip is open, -1 for none in this row.
   */
  private writeRow(times: RowTimes, elements: readonly (StatusElements | null)[], shown: Int32Array, frozen: boolean, now: number, tip: number): void {
    for (let index = 0; index < times.count; index += 1) {
      const icon = elements[index];
      if (icon === null || icon === undefined) {
        continue;
      }
      const left = frozen ? (times.left[index] as number) : Math.min(times.left[index] as number, (times.end[index] as number) - now);
      const before = shown[index] as number;
      const seconds = Math.max(1, stepSeconds(left, before < 0 ? 0 : before >> 1));
      const expiring = !frozen && times.expires[index] === 1 && left <= EXPIRING_MS ? 1 : 0;
      const value = seconds * 2 + expiring;
      if (index === tip && seconds !== this.tipShown && this.tipTime !== null) {
        this.tipTime.textContent = TIP_TIME_TEXTS[seconds] as string;
        this.tipShown = seconds;
      }
      if (value === before) {
        continue;
      }
      if (before < 0 || (before & 1) !== expiring) {
        icon.icon.toggleAttribute("data-expiring", expiring === 1);
      }
      icon.time.textContent = TIME_LEFT_TEXTS[seconds] as string;
      shown[index] = value;
    }
  }

  /**
   * One animation frame: writes at the frame's time, or the held clock's, then asks for the next.
   *
   * @param time The frame's time on the page clock.
   */
  private readonly step = (time: number): void => {
    this.write(this.clock === null ? time : this.clock());
    this.frame = requestAnimationFrame(this.step);
  };
}
